import { METHODOLOGY_CONFIG } from "@ark/shared";

const BLACK_REPLACEMENT_COLOR = "#0F172A";
const PURE_BLACK = "#000000";
const SECTOR_OVERFLOW_COLOR = "#4B5563";
const PROGRESS_BAR_BACKGROUND_COLOR = "#F3F4F6";
// Neutral grey for the "Sonstige"/other bucket so it recedes behind the named categories.
export const OTHER_GROUP_COLOR = "#94a3b8";

export const getChartSectorOrder = (methodologyId: string | null | undefined): string[] => {
  if (!methodologyId) {
    return [];
  }
  const config = METHODOLOGY_CONFIG[methodologyId as keyof typeof METHODOLOGY_CONFIG];
  return config ? [...config.chartSectorOrder] : [];
};

export const normalizeSectorTitle = (title: string): string => {
  const normalizations: Record<string, string> = {
    "Private Haushalte": "Haushalte",
    GHD: "Gewerbe, Handel, Dienstleistung",
  };
  return normalizations[title] || title;
};

export const hslToHex = (h: number, s: number, l: number): string => {
  h /= 360;
  s /= 100;
  l /= 100;

  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };

  let r, g, b;
  if (s === 0) {
    r = g = b = l;
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  const toHex = (c: number) => {
    const hex = Math.round(c * 255).toString(16);
    return hex.length === 1 ? "0" + hex : hex;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

export const GAUGE_DEFAULT_REMAINING_COLOR = "#95999F";
export const GAUGE_DEFAULT_COMPLETED_COLOR = "#178267";

export const CHART_PALETTE = [
  { name: "blue", h: 240, s: 75, l: 40 },
  { name: "red", h: 340, s: 75, l: 40 },
  { name: "yellow", h: 40, s: 100, l: 40 },
  { name: "green", h: 165, s: 70, l: 30 },
  { name: "teal", h: 190, s: 70, l: 30 },
  { name: "orange", h: 25, s: 80, l: 35 },
  { name: "slate", h: 215, s: 30, l: 35 },
  { name: "violet", h: 280, s: 70, l: 40 },
  { name: "magenta", h: 320, s: 72, l: 42 },
  { name: "lime", h: 78, s: 68, l: 32 },
  { name: "cyan", h: 198, s: 78, l: 42 },
  { name: "maroon", h: 352, s: 60, l: 28 },
];

const CHART_PALETTE_HEX_COLORS: readonly string[] = CHART_PALETTE.map((entry) => hslToHex(entry.h, entry.s, entry.l));

const resolvePaletteColorList = (paletteColors?: readonly string[]): readonly string[] =>
  paletteColors && paletteColors.length > 0 ? paletteColors : CHART_PALETTE_HEX_COLORS;

interface ResolvePaletteColorParams {
  index: number;
  count: number;
  paletteColors?: readonly string[];
  overflowColor?: string;
}

export const getPaletteColorCount = (paletteColors?: readonly string[]): number =>
  resolvePaletteColorList(paletteColors).length;

export const getPaletteSlotColor = (index: number, paletteColors?: readonly string[]): string => {
  const colors = resolvePaletteColorList(paletteColors);
  const color = colors[index % colors.length];
  return color === PURE_BLACK ? BLACK_REPLACEMENT_COLOR : color;
};

const resolvePaletteColor = ({ index, count, paletteColors, overflowColor }: ResolvePaletteColorParams): string => {
  if (index === -1 || count === 0) {
    return BLACK_REPLACEMENT_COLOR;
  }
  if (overflowColor !== undefined && index === count - 1 && count > getPaletteColorCount(paletteColors)) {
    return overflowColor;
  }
  return getPaletteSlotColor(index, paletteColors);
};


export const getChartElementColor = (
  elementName: string,
  elementKeys: string[],
  paletteColors?: readonly string[],
): string => resolvePaletteColor({ index: elementKeys.indexOf(elementName), count: elementKeys.length, paletteColors });

export const getEnergySourceColor = getChartElementColor;

export const getSectorColor = (sectorName: string, sectorKeys: string[], paletteColors?: readonly string[]): string =>
  resolvePaletteColor({
    index: sectorKeys.indexOf(sectorName),
    count: sectorKeys.length,
    paletteColors,
    overflowColor: SECTOR_OVERFLOW_COLOR,
  });

export const CHART_PATTERN_TYPES = ["stripes", "dots", "squares"] as const;
type ChartPatternType = (typeof CHART_PATTERN_TYPES)[number];

interface ChartPatternDefBase {
  readonly id: string;
  readonly background: string;
  readonly color: string;
}

interface LinesChartPatternDef extends ChartPatternDefBase {
  readonly type: "patternLines";
  readonly rotation: number;
  readonly lineWidth: number;
  readonly spacing: number;
}

interface DotsChartPatternDef extends ChartPatternDefBase {
  readonly type: "patternDots";
  readonly size: number;
  readonly padding: number;
  readonly stagger: boolean;
}

interface SquaresChartPatternDef extends ChartPatternDefBase {
  readonly type: "patternSquares";
  readonly size: number;
  readonly padding: number;
  readonly stagger: boolean;
}

export type ChartPatternDef = LinesChartPatternDef | DotsChartPatternDef | SquaresChartPatternDef;

export const createPatternDef = (patternId: string, patternType: ChartPatternType, color: string): ChartPatternDef => {
  if (patternType === "stripes") {
    return {
      id: patternId,
      type: "patternLines",
      background: color,
      color: "rgba(255, 255, 255, 0.8)",
      rotation: 45,
      lineWidth: 3,
      spacing: 4,
    };
  }
  if (patternType === "dots") {
    return {
      id: patternId,
      type: "patternDots",
      background: color,
      color: "rgba(255, 255, 255, 0.8)",
      size: 4,
      padding: 2,
      stagger: true,
    };
  }
  return {
    id: patternId,
    type: "patternSquares",
    background: color,
    color: "rgba(255, 255, 255, 0.8)",
    size: 4,
    padding: 1,
    stagger: true,
  };
};

interface PatternFill {
  readonly match: { readonly id: string };
  readonly id: string;
}

interface BuildPatternDefsParams {
  readonly items: readonly { readonly id: string; readonly color: string }[];
  readonly idPrefix: string;
}

interface PatternDefsResult {
  readonly defs: ChartPatternDef[];
  readonly fills: PatternFill[];
}

export const buildPatternDefs = ({ items, idPrefix }: BuildPatternDefsParams): PatternDefsResult => {
  const paletteLength = CHART_PALETTE.length;
  const defs: ChartPatternDef[] = [];
  const fills: PatternFill[] = [];
  items.forEach((item, index) => {
    if (index < paletteLength) {
      return;
    }
    const patternType = CHART_PATTERN_TYPES[(index - paletteLength) % CHART_PATTERN_TYPES.length];
    const patternId = `${idPrefix}-${patternType}-${index}`;
    defs.push(createPatternDef(patternId, patternType, item.color));
    fills.push({ match: { id: item.id }, id: patternId });
  });
  return { defs, fills };
};

export const getProgressBarColors = (): { background: string; indicator: string } => {
  const blueColor = CHART_PALETTE[0];
  const indicatorColor = hslToHex(blueColor.h, blueColor.s, blueColor.l);
  return {
    background: PROGRESS_BAR_BACKGROUND_COLOR,
    indicator: indicatorColor === PURE_BLACK ? BLACK_REPLACEMENT_COLOR : indicatorColor,
  };
};
